import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Button,
    ModalsProvider,
    SessionExpiredModal,
} from '@speeki/global-ui-components';

import { ContactSupportModal } from '@components/organisms/ContactSupportModal/ContactSupportModal';
import { MobileCaseStatusModal } from '@components/organisms/MobileCaseStatusModal/MobileCaseStatusModal';
import { MobileCompanyInformationModal } from '@components/organisms/MobileCompanyInformationModal/MobileCompanyInformationModal';
import { MobileCompanyPoliciesModal } from '@components/organisms/MobileCompanyPoliciesModal/MobileCompanyPoliciesModal';
import { MobileLoginModal } from '@components/organisms/MobileLoginModal/MobileLoginModal';
import { MobileRedirectModal } from '@components/organisms/MobileRedirectModal/MobileRedirectModal';
import { MobileStartToModal } from '@components/organisms/MobileStartToModal/MobileStartToModal';

import { useModalRedirect } from '@utils/hooks/useModalRedirect';

import { DownloadSpeekiAppModal } from '@domains/auth/organisms/DownloadSpeekiAppModal/DownloadSpeekiAppModal';
import { TermsAndConditionsInfoModal } from '@domains/auth/organisms/TermsAndConditionsInfoModal/TermsAndConditionsInfoModal';
import { SwitchCompanyModal } from '@domains/dashboard/modals/SwitchCompanyModal/SwitchCompanyModal';
import { ChangePersonalInfoModal } from '@domains/disclosures/modals/ChangePersonalInfoModal/ChangePersonalInfoModal';
import { GuestInfoModal } from '@domains/disclosures/modals/GuestInfoModal/GuestInfoModal';
import { ConversationDetailsGoBackModal } from '@domains/new-conversation/organisms/ConversationDetailsGoBackModal/ConversationDetailsGoBackModal';
import { IncidentTypesModal } from '@domains/new-conversation/organisms/IncidentTypesModal/IncidentTypesModal';
import { LeaveConfirmationModal } from '@domains/new-conversation/organisms/LeaveConfirmationModal/LeaveConfirmationModal';
import { ReportDetailsModal } from '@domains/new-conversation/organisms/ReportDetailsModal/ReportDetailsModal';
import { SelectSubsidiarySitesModal } from '@domains/new-conversation/organisms/SelectSubsidiarySitesModal/SelectSubsidiarySitesModal';
import { CompanyChangeModal } from '@domains/settings/modals/CompanyChangeModal/CompanyChangeModal';
import { EntityChangeModal } from '@domains/settings/modals/EntityChangeModal/EntityChangeModal';

export const AppModals = memo(() => {
    const { t } = useTranslation();
    const { handleModalRedirect } = useModalRedirect();

    return (
        <ModalsProvider
            modals={{
                CompanyChangeModal: (props) => (
                    <CompanyChangeModal {...props} />
                ),
                ContactSupportModal: (props) => (
                    <ContactSupportModal {...props} />
                ),
                ConversationDetailsGoBackModal: (props) => (
                    <ConversationDetailsGoBackModal {...props} />
                ),
                DownloadSpeekiAppModal: (props) => (
                    <DownloadSpeekiAppModal {...props} />
                ),
                EntityChangeModal: (props) => <EntityChangeModal {...props} />,
                GuestInfoModal: (props) => <GuestInfoModal {...props} />,
                IncidentTypesModal: (props) => (
                    <IncidentTypesModal {...props} />
                ),
                LeaveConfirmationModal: (props) => (
                    <LeaveConfirmationModal {...props} />
                ),
                MobileCaseStatusModal: (props) => (
                    <MobileCaseStatusModal {...props} />
                ),
                MobileCompanyInformationModal: (props) => (
                    <MobileCompanyInformationModal {...props} />
                ),
                MobileCompanyPoliciesModal: (props) => (
                    <MobileCompanyPoliciesModal {...props} />
                ),
                MobileDisclosuresModal: (props) => (
                    <MobileRedirectModal
                        {...props}
                        buttons={[
                            <Button
                                fullWidth
                                onClick={() =>
                                    handleModalRedirect({
                                        modalName: 'MobileDisclosuresModal',
                                        path: '/auth/declare-or-disclose-information',
                                    })
                                }
                                testId="button_submit-new-form"
                                variant="primary"
                            >
                                {t('SUBMIT_NEW_FORM')}
                            </Button>,
                            <Button
                                fullWidth
                                onClick={() =>
                                    handleModalRedirect({
                                        modalName: 'MobileDisclosuresModal',
                                        path: '/auth/declare-or-disclose-information',
                                    })
                                }
                                testId="button_see-my-forms"
                                variant="secondary"
                            >
                                {t('SEE_MY_FORMS')}
                            </Button>,
                        ]}
                        icon="SvgDAndD"
                        text={t('DISCLOSURES_ACTION_SUBTITLE')}
                    />
                ),
                MobileLoginModal: (props) => <MobileLoginModal {...props} />,
                MobileMessagingModal: (props) => (
                    <MobileRedirectModal
                        {...props}
                        buttons={[
                            <Button
                                fullWidth
                                onClick={() =>
                                    handleModalRedirect({
                                        modalName: 'MobileMessagingModal',
                                        path: '/auth/start-new-report',
                                    })
                                }
                                testId="button_start-new-report"
                                variant="primary"
                            >
                                {t('START_NEW_REPORT')}
                            </Button>,
                            <Button
                                fullWidth
                                onClick={() =>
                                    handleModalRedirect({
                                        modalName: 'MobileMessagingModal',
                                        path: '/auth/see-my-incidents',
                                    })
                                }
                                testId="button_see-my-cases"
                                variant="secondary"
                            >
                                {t('SEE_MY_CASES')}
                            </Button>,
                        ]}
                        icon="SvgMessaging"
                        text={t('MESSAGING_ACTION_SUBTITLE')}
                    />
                ),
                MobileStartToModal,
                MobileSurveysModal: (props) => (
                    <MobileRedirectModal
                        {...props}
                        buttons={[
                            <Button
                                fullWidth
                                onClick={() =>
                                    handleModalRedirect({
                                        modalName: 'MobileSurveysModal',
                                        path: '/auth/see-active-surveys',
                                    })
                                }
                                testId="button_see-active-surveys"
                                variant="primary"
                            >
                                {t('SEE_ACTIVE_SURVEYS')}
                            </Button>,
                        ]}
                        icon="SvgSurvey"
                        text={t('SURVEYS_ACTION_SUBTITLE')}
                    />
                ),
                PersonalInfoChangeModal: (props) => (
                    <ChangePersonalInfoModal {...props} />
                ),
                ReportDetailsModal: (props) => (
                    <ReportDetailsModal {...props} />
                ),
                SelectSubsidiarySitesModal: (props) => (
                    <SelectSubsidiarySitesModal {...props} />
                ),
                SessionExpiredModal: (props) => (
                    <SessionExpiredModal
                        {...props}
                        buttonText={t('SESSION_EXPIRED_BUTTON')}
                        description={t('SESSION_EXPIRED_DESC')}
                        title={t('SESSION_EXPIRED_TITLE')}
                    />
                ),
                SwitchCompanyModal: (props) => (
                    <SwitchCompanyModal {...props} />
                ),
                TermsAndConditionsInfoModal: (props) => (
                    <TermsAndConditionsInfoModal {...props} />
                ),
            }}
        />
    );
});
